import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios'; // axios를 사용하여 백엔드 API와 통신

// 백엔드 기본 주소 설정 (실제 본인의 Render 주소로 바꾸세요)
// const - c언어의 상수포인터랑 같은 개념
const API_URL = 'https://the-pivot-logical-battleground.onrender.com/api/posts';

// 1. 게시글 가져오기 (topicId와 cycleId를 객체로 받음)
export const fetchPosts = createAsyncThunk(
  'posts/fetchPosts', 
  async ({ topicId, cycleId, sortBy }, {rejectWithValue}) => {
    try {
      // 쿼리 파라미터를 포함한 URL 구성
      let url = `${API_URL}/${topicId}?`;
      if (cycleId) url += `cycleId=${cycleId}&`;
      if (sortBy) url += `sortBy=${sortBy}`;

      // 🚩 추가: 로컬 스토리지에서 토큰 가져오기
      const token = localStorage.getItem('token');

      // 🚩 토큰이 있을 때만 헤더 설정, 없으면 빈 객체로 요청
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.get(url, config);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// 2. 게시글 생성 (authorId가 포함된 postData를 받음)
export const createPost = createAsyncThunk('posts/createPost', async (postData, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(API_URL, postData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;

  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

// 3. 좋아요 토글 추가
export const toggleLike = createAsyncThunk('posts/toggleLike', async ({ postId }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/${postId}/like`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data; // 업데이트된 post 객체 반환

  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});



// 4. 게시글 삭제 추가
export const deletePost = createAsyncThunk('posts/deletePost', async ({ postId }) => {
  const token = localStorage.getItem('token');
  await axios.delete(`${API_URL}/${postId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return {postId}; // 삭제된 ID 반환
});



const postsSlice = createSlice({
  name: 'posts', // 함수 이름의 공통부분 'posts/'
  initialState: { list: [], status: 'idle' }, // 초기 상태
  reducers: {}, // 내부처리용 리듀서(현재는 없음)
  
  // 외부처리용 리듀서
  extraReducers: (builder) => {
    builder
      // 목록 불러오기 성공
      .addCase(fetchPosts.fulfilled, (state, action) => { 
        state.list = action.payload;  // state: 현재 슬라이스의 상태 (초기값에서 변화해온 현재 모습) , action.payload: 서버에서 새로 가져온 게시글 배열 전체
      })

      // 게시글 생성 성공 (맨 뒤에 추가 - push ; unshift는 맨 앞에 추가)
      .addCase(createPost.fulfilled, (state, action) => { 
        state.list.unshift(action.payload); 
      })

      // 좋아요 업데이트 성공 (해당 게시글만 교체)
      .addCase(toggleLike.fulfilled, (state, action) => {
        console.log("action:", action);
        console.log("action.payload:", action.payload);

        const index = state.list.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.list[index] = action.payload; // 서버에서 반환된 전체 게시글 객체로 교체
        }
      })

      // 삭제 성공 (목록에서 제거)
      .addCase(deletePost.fulfilled, (state, action) => {
        state.list = state.list.filter(p => p._id !== action.payload.postId);
      })

      // 댓글 작성 성공 시 목록의 댓글 수 업데이트 commentsSlice의 액션 감지
      .addMatcher(
        (action) => action.type === 'comments/createComment/fulfilled',
        (state, action) => {
          const postId = action.payload?.postId;
        
          // 🛡️ 만약 postId를 못 찾았다면 목록을 건드리지 않고 즉시 종료 (사라짐 방지)
          if (!postId || !state.list || state.list.length === 0) {
            console.warn("게시글 목록이 비어있거나 postId가 없어 동기화를 건너뜁니다.");
            return; 
          }

          // 🛡️ 기존 list 배열을 순회하며 해당 게시글의 숫자만 안전하게 교체
          state.list = state.list.map(post => {
            if (post._id === postId) {
              return {
                ...post,
                commentCount: (post.commentCount || 0) + 1
              };
            }
            return post;
          });
        
          console.log("✅ 댓글 작성 후 리스트 보존 확인:", state.list);
        }
      )

      .addMatcher(
        (action) => action.type === 'comments/deleteComment/fulfilled',
        (state, action) => {
          console.log("백엔드 응답 페이로드:", action.payload);

          const postId = action.payload?.postId;

          // 🛡️ 만약 postId를 못 찾았다면 목록을 건드리지 않고 즉시 종료 (사라짐 방지)
          if (!postId || !state.list || state.list.length === 0) {
            console.warn("게시글 목록이 비어있거나 postId가 없어 동기화를 건너뜁니다.");
            return; 
          }

          // 🛡️ 기존 list 배열을 순회하며 해당 게시글의 숫자만 안전하게 교체
          state.list = state.list.map(post => {
            if (post._id === postId._id) {
              return {
                ...post,
                commentCount: (post.commentCount || 0) - 1
              };
            }
            return post;
          });
        }
      );
  }
});

export default postsSlice.reducer;